/*
 * Purpose: closes Galleria's mobile modal after a Drop anchor is selected and transfers focus to that section.
 * Dependencies: Galleria's base component and modal closePopup event.
 * Rendered by: sections/drop-header.liquid on storefront pages while Drop Mode is enabled.
 * Related data: anchor links emitted by snippets/drop-navigation.liquid.
 */

(async () => {
  const componentName = 'drop-navigation';
  const modalTransitionFallbackMs = 300;
  const modalTransitionBufferMs = 20;

  if (!window.customElements || customElements.get(componentName)) return;

  const baseComponentUrl = window.theme?.modules?.baseComponent;
  if (!baseComponentUrl) {
    console.warn('Drop navigation could not initialize because the Galleria base component is unavailable.');
    return;
  }

  try {
    const baseComponent = await import(baseComponentUrl);

    if (customElements.get(componentName)) return;

    customElements.define(componentName, class extends baseComponent.default {
      render() {
        if (!this.hasAttribute('mobile')) return;

        this.addEventListener('click', (event) => {
          if (!(event.target instanceof Element)) return;

          const link = event.target.closest('a[href^="#"]');
          const target = this._getAnchorTarget(link);
          if (!link || !target) return;

          event.preventDefault();
          this.trigger('closePopup');

          window.setTimeout(() => {
            this._focusAnchorTarget(target, link.hash);
          }, this._getModalTransitionDelay());
        });
      }

      _getAnchorTarget(link) {
        if (!link?.hash) return null;

        try {
          return document.getElementById(decodeURIComponent(link.hash.slice(1)));
        } catch (error) {
          console.warn('Drop navigation received an invalid anchor.', error);
          return null;
        }
      }

      _focusAnchorTarget(target, hash) {
        const focusTarget = target.matches('details') ? target.querySelector('summary') : target;
        if (!focusTarget) return;

        if (!focusTarget.matches('a, button, input, select, textarea, summary, [tabindex]')) {
          focusTarget.setAttribute('tabindex', '-1');
        }

        window.history.pushState(null, '', hash);
        try {
          focusTarget.focus({ preventScroll: true });
        } catch (error) {
          focusTarget.focus();
        }
        target.scrollIntoView({ block: 'start', behavior: 'auto' });
      }

      _getModalTransitionDelay() {
        if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return 0;

        const modal = document.querySelector('modal-popup');
        if (!modal) return modalTransitionFallbackMs + modalTransitionBufferMs;

        const transitionMs = Number.parseFloat(
          window.getComputedStyle(modal).getPropertyValue('--transition')
        );

        return Number.isFinite(transitionMs)
          ? transitionMs + modalTransitionBufferMs
          : modalTransitionFallbackMs + modalTransitionBufferMs;
      }
    });
  } catch (error) {
    console.warn('Drop navigation could not initialize.', error);
  }
})();
